import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

/**
 * Custom hook that handles URL transitions for backward compatibility
 * It ensures both old URLs (with spaces) and new URLs (with slugs) work
 * by redirecting to the canonical URL form if needed
 * 
 * @param {Object} options - Configuration options
 * @param {String} options.currentSlug - The slug from the data
 * @param {String} options.currentName - The original name 
 * @param {String} options.urlParam - The URL parameter from useParams()
 * @param {String} options.basePath - The base path (e.g., '/brand', '/collection')
 * @param {Boolean} options.shouldRedirect - Whether to redirect to canonical URL
 * @returns {Boolean} - Whether a redirect is happening
 */
export const useBackwardCompatibleUrl = ({
  currentSlug,
  currentName,
  urlParam,
  basePath,
  shouldRedirect = true
}) => {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Only proceed if we have all the necessary data
    if (!currentSlug || !urlParam) return;

    // If URL parameter is already the slug, we're good
    if (urlParam === currentSlug) return;

    // If URL parameter is the name and we should redirect to slug
    if (shouldRedirect && urlParam === currentName || decodeURIComponent(urlParam) === currentName) {
      navigate(`${basePath}/${currentSlug}`, { replace: true });
    }
  }, [currentSlug, currentName, urlParam, basePath, navigate, shouldRedirect]);

  // Return whether we're in a non-canonical URL (for potential UI flags)
  return urlParam !== currentSlug;
};

export default useBackwardCompatibleUrl;
